import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Search } from 'lucide-react';
import {
  COUNTRY_OPTIONS,
  DEFAULT_COUNTRY_ISO,
  findCountry,
  splitPhone,
} from './countryDialCodes';
import './PhoneInput.css';

/**
 * Phone field with a searchable country picker.
 *
 * The country code is chosen from a list with flags rather than typed, so a
 * merchant cannot save a number without one, and cannot mistype the code.
 *
 * value/onChange speak a single "+<dial> <national>" string, which is what the
 * API and the varchar(20) column already store — the split is presentation
 * only.
 *
 * The selected country is held locally rather than derived from `value` on
 * every render, because several countries share a dial code (+1 is US, Canada
 * and much of the Caribbean) and re-deriving would silently snap the user's
 * choice back to whichever shares it first.
 *
 * `onCountryChange(iso)` fires when the picker's country changes, so a caller
 * that keeps its own country field can follow it. The ISO is reported rather
 * than the dial code precisely because the dial code does not identify a
 * country.
 */
const PhoneInput = ({
  value = '',
  onChange,
  onCountryChange,
  id = 'phone',
  error = '',
  disabled = false,
  maxNationalDigits = 14,
}) => {
  const initial = useMemo(() => splitPhone(value), [value]);

  const [iso, setIso] = useState(initial.iso || DEFAULT_COUNTRY_ISO);
  const [national, setNational] = useState(initial.national);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const wrapRef = useRef(null);
  const searchRef = useRef(null);
  const listRef = useRef(null);
  // What we last handed to onChange, so an echo of our own value is ignored.
  const emittedRef = useRef(value);

  const country = findCountry(iso);

  const compose = useCallback(
    (dial, digits) => (digits ? `+${dial} ${digits}` : `+${dial}`),
    []
  );

  // Re-sync when the parent supplies a genuinely different number — the
  // profile loads asynchronously, so the first render sees an empty string.
  useEffect(() => {
    if (value === emittedRef.current) return;
    const next = splitPhone(value);
    setIso(next.iso || DEFAULT_COUNTRY_ISO);
    setNational(next.national);
    emittedRef.current = value;
  }, [value]);

  const emit = useCallback(
    (nextIso, nextNational) => {
      const dial = findCountry(nextIso).dial;
      const composed = compose(dial, nextNational);
      emittedRef.current = composed;
      onChange?.(composed);
    },
    [compose, onChange]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase().replace(/^\+/, '');
    if (!q) return COUNTRY_OPTIONS;
    return COUNTRY_OPTIONS.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.iso.toLowerCase().startsWith(q) ||
        c.dial.startsWith(q)
    );
  }, [query]);

  // Close on an outside press and on Escape.
  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  useEffect(() => {
    if (open) searchRef.current?.focus();
    else {
      setQuery('');
      setActiveIndex(0);
    }
  }, [open]);

  // Keep the highlighted row in view while arrowing through the list.
  useEffect(() => {
    if (!open) return;
    const row = listRef.current?.children?.[activeIndex];
    // Not implemented in every environment (jsdom, some embedded webviews).
    if (typeof row?.scrollIntoView === 'function') {
      row.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex, open]);

  const selectCountry = (nextIso) => {
    setIso(nextIso);
    setOpen(false);
    emit(nextIso, national);
    // After emit, so a caller that rewrites `value` in response wins over the
    // number we just composed.
    onCountryChange?.(nextIso);
  };

  const onSearchKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const pick = filtered[activeIndex];
      if (pick) selectCountry(pick.iso);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    }
  };

  const onNationalChange = (e) => {
    // Digits only: the country code lives in the picker, so a "+" or letters
    // typed here are always a mistake (including a browser autofilling an
    // email address into the field).
    const digits = e.target.value.replace(/\D/g, '').slice(0, maxNationalDigits);
    setNational(digits);
    emit(iso, digits);
  };

  return (
    <div className={`mp-phone ${error ? 'is-error' : ''} ${disabled ? 'is-disabled' : ''}`} ref={wrapRef}>
      <button
        type="button"
        className="mp-phone__country"
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Country code: ${country.name} +${country.dial}`}
      >
        <span className="mp-phone__flag" aria-hidden="true">{country.flag}</span>
        <span className="mp-phone__dial">+{country.dial}</span>
        <ChevronDown size={14} className="mp-phone__chev" aria-hidden="true" />
      </button>

      <input
        id={id}
        name="phone"
        className="mp-phone__number"
        type="tel"
        inputMode="numeric"
        autoComplete="tel-national"
        value={national}
        onChange={onNationalChange}
        disabled={disabled}
        placeholder="98765 43210"
        aria-invalid={Boolean(error)}
      />

      {open && (
        <div className="mp-phone__pop">
          <div className="mp-phone__search">
            <Search size={14} aria-hidden="true" />
            <input
              ref={searchRef}
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setActiveIndex(0);
              }}
              onKeyDown={onSearchKeyDown}
              placeholder="Search country or code"
              aria-label="Search country or dial code"
            />
          </div>

          <ul className="mp-phone__list" role="listbox" ref={listRef}>
            {filtered.map((c, i) => (
              <li key={c.iso}>
                <button
                  type="button"
                  role="option"
                  aria-selected={c.iso === iso}
                  className={`mp-phone__opt ${i === activeIndex ? 'is-active' : ''} ${
                    c.iso === iso ? 'is-selected' : ''
                  }`}
                  onMouseEnter={() => setActiveIndex(i)}
                  onClick={() => selectCountry(c.iso)}
                >
                  <span className="mp-phone__flag" aria-hidden="true">{c.flag}</span>
                  <span className="mp-phone__name">{c.name}</span>
                  <span className="mp-phone__optdial">+{c.dial}</span>
                  {c.iso === iso && <Check size={14} aria-hidden="true" />}
                </button>
              </li>
            ))}
            {filtered.length === 0 && (
              <li className="mp-phone__none">No country matches that</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default PhoneInput;
